import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, message, Modal, Spin } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import s from './s.module.less';
import PaymentContent from 'components/PaymentContent';
import commonS from 'styles/common.module.less';
import useGetPPAPaymentInfo from 'hooks/useGetPPAPaymentInfo';
import useProviderHomeInfo from 'hooks/useProviderHomeInfo';
import { joinKlarityHealth, joinKlarityHealthWithoutPayment, savePaymentInfo } from 'api/ppaOnboarding';
import useGetPPALeadGenFee from 'hooks/useGetPPALeadGenFee';
import useProviderKlarityServiceList from 'hooks/useProviderKlarityServiceList';
import LeadGenFeeTable from 'components/LeadGenFeeTable';
import WebinarBooking from 'components/WebinarBooking';
import { CALENDLY_LINK } from 'constants/common';
import { EWebinarScheduleType } from 'types/common';

interface IProps {
    setNextButtonDisabled?: (s: boolean) => void;
    // 父级 STEP_13 的 Next 拦截：case 3（有卡未 list）时返回 true 并弹 without-payment 弹窗，阻止默认跳转
    nextInterceptRef?: React.MutableRefObject<(() => boolean) | null>;
    // case 3 without-payment list 成功后：让父级直接弹出 Pro-Listings（一次 Next 走完）
    onListedWithoutPayment?: () => void;
}

const Step5 = ({
    setNextButtonDisabled,
    nextInterceptRef,
    onListedWithoutPayment,
}: IProps) => {
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    // 无付款 list 模式：弹窗去掉 payment 表单，「List me on Klarity Health」走 without-payment 接口
    const [listWithoutPayment, setListWithoutPayment] = useState(false);
    const [showWebinarModal, setShowWebinarModal] = useState(false);
    const [paymentInfo, loading, , refetch] = useGetPPAPaymentInfo();
    const [homeInfo, , refetchHome] = useProviderHomeInfo();
    const [leadGenFee, leadGenFeeLoading, refetchLeadGenFee] = useGetPPALeadGenFee();
    const [isEdit, setIsEdit] = useState(false);
    const [allServices, allServicesLoading, refetchAllServices] = useProviderKlarityServiceList();

    const leadGenFeeConfigs = leadGenFee?.leadGenFeeConfigs || [];
    const [statesAndFeeMap, setStatesAndFeeMap] = useState<{ states: string[], fee: number }[]>([]);
    // Mock payment information - in real app this would come from API
    const hasPaymentInfo = !!paymentInfo; // Set to false to show "Add payment method" button
    // 是否已 list 在 Klarity（provider home 的 ppaProvider）
    const isListed = !!homeInfo?.ppaProvider;

    useEffect(() => {
        setNextButtonDisabled?.(!hasPaymentInfo);
    }, [hasPaymentInfo]);

    // case 3：已填卡（hasPaymentInfo）但未 list（!isListed）时，拦截父级 Next → 弹 without-payment 弹窗
    useEffect(() => {
        if (!nextInterceptRef) return undefined;
        nextInterceptRef.current = () => {
            if (hasPaymentInfo && !isListed) {
                setIsEdit(false);
                setListWithoutPayment(true);
                setShowPaymentModal(true);
                return true;
            }
            return false;
        };
        return () => {
            nextInterceptRef.current = null;
        };
    }, [nextInterceptRef, hasPaymentInfo, isListed]);

    React.useEffect(() => {
        const configs = leadGenFee?.leadGenFeeConfigs;
        if (configs && configs.length > 0 && allServices && allServices.length > 0) {
            const allStates: string[] = [];
            allServices.forEach((service) => {
                const matched = service.serviceSpecialtyName === leadGenFee?.specialty;
                if (matched) {
                    if (!allStates.includes(service.state)) {
                        allStates.push(service.state);
                    }
                }
            });
            const _statesAndFeeMap: { states: string[], fee: number }[] = [];
            allStates.forEach((state) => {
                const targetConfig = configs.find((config) => config.state === state);
                if (targetConfig) {
                    const fee = targetConfig.leadGenFee;
                    const targetItem = _statesAndFeeMap.find((item) => item.fee === fee);
                    if (targetItem) {
                        targetItem.states.push(state);
                    } else {
                        _statesAndFeeMap.push({
                            states: [state],
                            fee,
                        });
                    }
                }
            });
            setStatesAndFeeMap(_statesAndFeeMap.sort((a, b) => a.fee - b.fee));
        }
    }, [leadGenFee, allServices]);
    const handleAddPaymentMethod = useCallback(() => {
        setIsEdit(false);
        setListWithoutPayment(false);
        setShowPaymentModal(true);
    }, []);

    const handleJoinKlarityHealth = async (paymentMethodId: string) => {
        try {
            const res = await joinKlarityHealth(paymentMethodId);
            if (res && !res.error) {
                setShowPaymentModal(false);
                refetch();
                // list 成功后 ppaProvider 变 true，刷新 home，避免下次 Next 又落到 case 3
                refetchHome();
            } else {
                message.error(res?.error);
            }
        } catch (e) {
            console.error(e);
        }
    };

    // case 3：已有卡、未 list —— 不新增付款方式，直接用已有卡 list on Klarity Health
    const handleListWithoutPayment = async () => {
        try {
            const res = await joinKlarityHealthWithoutPayment();
            if (res && !res.error) {
                setShowPaymentModal(false);
                setListWithoutPayment(false);
                refetch();
                refetchHome();
                // list 成功后直接弹 Pro-Listings，一次 Next 走完整个流程
                onListedWithoutPayment?.();
            } else {
                message.error(res?.error);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const handlePaymentMethodCreated = async (paymentMethodId: string) => {
        try {
            await savePaymentInfo({
                paymentMethodId,
            });

            if (!isEdit) {
                handleJoinKlarityHealth(paymentMethodId);
            } else {
                setShowPaymentModal(false);
                refetch();
            }
        } catch (error) {
            console.error('Save payment information failed:', error);
        }
    };

    const handleEditPayment = useCallback(() => {
        setIsEdit(true);
        setListWithoutPayment(false);
        setShowPaymentModal(true);
    }, []);

    const featureItems = useMemo(() => {
        return [
            {
                id: 2,
                icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M5.64001 5.63982C9.15001 2.12982 14.85 2.12982 18.37 5.63982C21.88 9.14982 21.88 14.8498 18.37 18.3698C14.86 21.8798 9.16001 21.8798 5.64001 18.3698C2.13001 14.8598 2.13001 9.15982 5.64001 5.63982Z" stroke="#F97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M6 6L18 18" stroke="#F97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                ),
                title: 'No extra charges',
                description: 'There are no charges for follow-up visits. You\'re only billed once per new patient lead.',
            },
            {
                id: 3,
                icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M12.5 22.9844C12.376 22.9844 12.2609 22.9755 12.1547 22.9578C12.0484 22.9401 11.9422 22.9135 11.8359 22.8781C9.44531 22.0812 7.54167 20.6072 6.125 18.456C4.70833 16.3048 4 13.9892 4 11.5094V6.48906C4 6.04635 4.12856 5.64792 4.38569 5.29375C4.64281 4.93958 4.97467 4.68281 5.38125 4.52344L11.7562 2.13281C12.0042 2.04427 12.2521 2 12.5 2C12.7479 2 12.9958 2.04427 13.2437 2.13281L19.6187 4.52344C20.026 4.68281 20.3582 4.93958 20.6154 5.29375C20.8725 5.64792 21.0007 6.04635 21 6.48906V11.5094C21 12.8375 20.7921 14.1348 20.3763 15.4013C19.9605 16.6678 19.3627 17.8142 18.5828 18.8406C18.4411 19.0354 18.251 19.1374 18.0122 19.1466C17.7735 19.1558 17.5653 19.0715 17.3875 18.8937L14.625 16.1312C14.3062 16.326 13.9655 16.4723 13.6029 16.5701C13.2402 16.6678 12.8726 16.7163 12.5 16.7156C11.3312 16.7156 10.3307 16.2995 9.49844 15.4672C8.66615 14.6349 8.25 13.6344 8.25 12.4656C8.25 11.2969 8.66615 10.2964 9.49844 9.46406C10.3307 8.63177 11.3312 8.21562 12.5 8.21562C13.6687 8.21562 14.6693 8.63177 15.5016 9.46406C16.3339 10.2964 16.75 11.2969 16.75 12.4656C16.75 12.8552 16.7015 13.2317 16.6044 13.5951C16.5074 13.9584 16.3611 14.308 16.1656 14.6437L17.7594 16.2375C18.1135 15.5115 18.388 14.75 18.5828 13.9531C18.7776 13.1562 18.875 12.3417 18.875 11.5094V6.48906L12.5 4.09844L6.125 6.48906V11.5094C6.125 13.6521 6.72708 15.6 7.93125 17.3531C9.13542 19.1062 10.6583 20.275 12.5 20.8594C12.7833 20.7708 13.0624 20.6646 13.3372 20.5406C13.6121 20.4167 13.882 20.275 14.1469 20.1156C14.3948 19.974 14.6647 19.9208 14.9565 19.9562C15.2483 19.9917 15.4831 20.1333 15.6609 20.3812C15.8387 20.6292 15.8961 20.8948 15.8331 21.1781C15.77 21.4615 15.6152 21.6828 15.3687 21.8422C15.0146 22.0547 14.6604 22.2452 14.3062 22.4138C13.9521 22.5824 13.5714 22.7372 13.1641 22.8781C13.0578 22.9135 12.9516 22.9401 12.8453 22.9578C12.7391 22.9755 12.624 22.9844 12.5 22.9844ZM12.5 14.5906C13.0844 14.5906 13.5848 14.3824 14.0013 13.9659C14.4178 13.5494 14.6257 13.0493 14.625 12.4656C14.6243 11.882 14.4164 11.3819 14.0013 10.9654C13.5862 10.5489 13.0858 10.3406 12.5 10.3406C11.9142 10.3406 11.4141 10.5489 10.9997 10.9654C10.5854 11.3819 10.3771 11.882 10.375 12.4656C10.3729 13.0493 10.5811 13.5497 10.9997 13.9669C11.4184 14.3841 11.9185 14.592 12.5 14.5906Z" fill="#F97316" />
                    </svg>
                ),
                title: 'Cancellation policy',
                description: 'You can set your own no-show/cancellation policy and decide to charge patients accordingly when those terms are not followed.',
            },
            {
                id: 4,
                icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M3 4.628L12.0045 2L21 4.628V10.017C20.9996 12.7788 20.1304 15.4704 18.5154 17.7108C16.9004 19.9512 14.6216 21.6267 12.0015 22.5C9.38048 21.627 7.10065 19.9514 5.48505 17.7105C3.86946 15.4696 3.00004 12.7771 3 10.0145V4.628Z" stroke="#F97316" strokeWidth="2" strokeLinejoin="round" />
                        <path d="M7.5 11.5L11 15L17 9" stroke="#F97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                ),
                title: 'Patient info & insurance',
                description: 'Klarity collects the patient\'s contact information and insurance details upon booking to help you verify eligibility in advance.',
            },
            {
                id: 5,
                icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M9.944 3.25H14.056C15.894 3.25 17.35 3.25 18.489 3.403C19.661 3.561 20.61 3.893 21.359 4.641C22.107 5.39 22.439 6.339 22.597 7.511C22.687 8.184 22.724 8.967 22.739 9.874C22.7514 9.95007 22.7528 10.0275 22.743 10.104C22.749 10.6693 22.7513 11.2827 22.75 11.944V12.056C22.75 13.894 22.75 15.35 22.597 16.489C22.439 17.661 22.107 18.61 21.359 19.359C20.61 20.107 19.661 20.439 18.489 20.597C17.349 20.75 15.894 20.75 14.056 20.75H9.944C8.106 20.75 6.65 20.75 5.511 20.597C4.339 20.439 3.39 20.107 2.641 19.359C1.893 18.61 1.561 17.661 1.403 16.489C1.25 15.349 1.25 13.894 1.25 12.056V11.944C1.24866 11.2827 1.251 10.6693 1.257 10.104C1.24691 10.0276 1.24792 9.95012 1.26 9.874C1.276 8.967 1.313 8.184 1.403 7.511C1.561 6.339 1.893 5.39 2.641 4.641C3.39 3.893 4.339 3.561 5.511 3.403C6.651 3.25 8.106 3.25 9.944 3.25ZM2.752 10.75C2.75066 11.138 2.75 11.5547 2.75 12C2.75 13.907 2.752 15.262 2.89 16.29C3.025 17.295 3.279 17.875 3.702 18.298C4.125 18.721 4.705 18.975 5.711 19.11C6.739 19.248 8.093 19.25 10 19.25H14C15.907 19.25 17.262 19.248 18.29 19.11C19.295 18.975 19.875 18.721 20.298 18.298C20.721 17.875 20.975 17.295 21.11 16.289C21.248 15.261 21.25 13.907 21.25 12C21.25 11.5547 21.2493 11.138 21.248 10.75H2.752ZM21.224 9.25H2.776C2.796 8.663 2.83 8.156 2.89 7.71C3.025 6.705 3.279 6.125 3.702 5.702C4.125 5.279 4.705 5.025 5.711 4.89C6.739 4.752 8.093 4.75 10 4.75H14C15.907 4.75 17.262 4.752 18.29 4.89C19.295 5.025 19.875 5.279 20.298 5.702C20.721 6.125 20.975 6.705 21.11 7.711C21.17 8.156 21.204 8.663 21.224 9.25ZM5.25 16C5.25 15.8011 5.32902 15.6103 5.46967 15.4697C5.61032 15.329 5.80109 15.25 6 15.25H10C10.1989 15.25 10.3897 15.329 10.5303 15.4697C10.671 15.6103 10.75 15.8011 10.75 16C10.75 16.1989 10.671 16.3897 10.5303 16.5303C10.3897 16.671 10.1989 16.75 10 16.75H6C5.80109 16.75 5.61032 16.671 5.46967 16.5303C5.32902 16.3897 5.25 16.1989 5.25 16ZM11.75 16C11.75 15.8011 11.829 15.6103 11.9697 15.4697C12.1103 15.329 12.3011 15.25 12.5 15.25H14C14.1989 15.25 14.3897 15.329 14.5303 15.4697C14.671 15.6103 14.75 15.8011 14.75 16C14.75 16.1989 14.671 16.3897 14.5303 16.5303C14.3897 16.671 14.1989 16.75 14 16.75H12.5C12.3011 16.75 12.1103 16.671 11.9697 16.5303C11.829 16.3897 11.75 16.1989 11.75 16Z" fill="#F97316" />
                    </svg>
                ),
                title: 'Credit card on file',
                description: 'We encourage patients to securely add a credit card on file via the patient portal to support enforcement of your cancellation policy.',
            },
        ];
    }, []);

    if (loading) {
        return <Spin spinning />;
    }

    return (
        <div className={s.wrap}>
            <Modal
                width="570px"
                open={showPaymentModal}
                className={`${commonS.modalFixHeightWrap} ${s.modal}`}
                footer={null}
                onCancel={() => {
                    setShowPaymentModal(false);
                    setListWithoutPayment(false);
                }}
                destroyOnClose
            >
                <PaymentContent
                    // buttonClassName={s.addPaymentBtn}
                    headerChildren={
                        <div className={s.modalTitle}>
                            <div>{listWithoutPayment ? 'List me on Klarity Health' : 'Add payment information'}</div>
                            <div className={s.stripTip} />
                        </div>
                    }
                    // case 3：去掉 payment 表单，只保留 Platform agreement，确认走 without-payment 接口
                    hidePayment={listWithoutPayment}
                    onConfirm={handleListWithoutPayment}
                    paymentMehodDescription="A temporary $0.10 charge will be applied to verify your payment method."
                    confirmButtonText="List me on Klarity Health"
                    onPaymentMethodCreated={handlePaymentMethodCreated}
                    onSuccess={() => {
                        setShowPaymentModal(false);
                        setListWithoutPayment(false);
                        refetch();
                    }}
                />
            </Modal>
            <WebinarBooking
                show={showWebinarModal}
                link={CALENDLY_LINK.KLARITY_ONBOARDING_HELP}
                webinarScheduleType={EWebinarScheduleType.KLARITY_ONBOARDING_HELP}
                onClose={() => {
                    setShowWebinarModal(false);
                }}
                title="Join webinar"
            />
            <div className={s.content}>
                {/* Header Section */}
                <div className={s.header}>
                    <h1>Add payment method to list on Klarity Health</h1>
                    <div className={s.headerDescription}>
                        Adding a payment method is a required step before you
                        can start receiving bookings.
                    </div>
                </div>

                {/* Webinar Reminder Banner - only show after payment method is added */}
                {hasPaymentInfo && (
                    <div className={s.webinarBanner}>
                        <div className={s.webinarIcon}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10ZM11 6C11 6.55228 10.5523 7 10 7C9.44772 7 9 6.55228 9 6C9 5.44772 9.44772 5 10 5C10.5523 5 11 5.44772 11 6ZM9 9C8.44772 9 8 9.44772 8 10C8 10.5523 8.44772 11 9 11V14C9 14.5523 9.44772 15 10 15H11C11.5523 15 12 14.5523 12 14C12 13.4477 11.5523 13 11 13V10C11 9.44772 10.5523 9 10 9H9Z" fill="#60A5FA" />
                            </svg>
                        </div>
                        <div className={s.webinarContent}>
                            <div className={s.webinarText}>
                                New to Klarity? Join a live webinar to learn what to expect after onboarding and how providers can start receiving bookings as early as the first week.
                            </div>
                            <Button
                                type="default"
                                className={s.webinarButton}
                                onClick={() => setShowWebinarModal(true)}
                            >
                                Join a webinar
                            </Button>
                        </div>
                    </div>
                )}

                {/* Features Grid */}
                <div className={s.leadgenfeeWrap}>
                    <div className={`${s.featureCard} ${s.leadgenfeeCard}`}>
                        <div className={s.iconWrapper}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M12 2.75C9.54676 2.75 7.19398 3.72455 5.45927 5.45926C3.72456 7.19397 2.75001 9.54675 2.75001 12C2.75001 13.481 3.09801 14.879 3.71501 16.118C3.96301 16.616 4.05801 17.21 3.90201 17.795L3.30601 20.02C3.28099 20.1132 3.28093 20.2114 3.30586 20.3046C3.33078 20.3979 3.37981 20.4829 3.44801 20.5512C3.51621 20.6195 3.60118 20.6687 3.69438 20.6937C3.78759 20.7188 3.88576 20.7189 3.97901 20.694L6.20601 20.098C6.76878 19.9537 7.36489 20.0202 7.88201 20.285C9.16111 20.9221 10.571 21.2525 12 21.25C14.4533 21.25 16.806 20.2754 18.5407 18.5407C20.2755 16.806 21.25 14.4533 21.25 12C21.25 9.54675 20.2755 7.19397 18.5407 5.45926C16.806 3.72455 14.4533 2.75 12 2.75ZM1.25001 12C1.25001 6.063 6.06301 1.25 12 1.25C17.937 1.25 22.75 6.063 22.75 12C22.75 17.937 17.937 22.75 12 22.75C10.282 22.75 8.65601 22.346 7.21301 21.628C7.02254 21.5281 6.8026 21.4997 6.59301 21.548L4.36701 22.143C2.84301 22.551 1.44901 21.157 1.85701 19.633L2.45301 17.407C2.50128 17.1974 2.4729 16.9775 2.37301 16.787C1.63215 15.3001 1.24767 13.6612 1.25001 12ZM12 7.25C12.1989 7.25 12.3897 7.32902 12.5303 7.46967C12.671 7.61032 12.75 7.80109 12.75 8V8.01C13.839 8.285 14.75 9.143 14.75 10.333C14.75 10.5319 14.671 10.7227 14.5303 10.8633C14.3897 11.004 14.1989 11.083 14 11.083C13.8011 11.083 13.6103 11.004 13.4697 10.8633C13.329 10.7227 13.25 10.5319 13.25 10.333C13.25 9.949 12.824 9.417 12 9.417C11.176 9.417 10.75 9.949 10.75 10.333C10.75 10.717 11.176 11.25 12 11.25C13.385 11.25 14.75 12.21 14.75 13.667C14.75 14.857 13.839 15.716 12.75 15.99V16C12.75 16.1989 12.671 16.3897 12.5303 16.5303C12.3897 16.671 12.1989 16.75 12 16.75C11.8011 16.75 11.6103 16.671 11.4697 16.5303C11.329 16.3897 11.25 16.1989 11.25 16V15.99C10.161 15.716 9.25001 14.857 9.25001 13.667C9.25001 13.4681 9.32903 13.2773 9.46968 13.1367C9.61033 12.996 9.8011 12.917 10 12.917C10.1989 12.917 10.3897 12.996 10.5303 13.1367C10.671 13.2773 10.75 13.4681 10.75 13.667C10.75 14.051 11.176 14.583 12 14.583C12.824 14.583 13.25 14.051 13.25 13.667C13.25 13.283 12.824 12.75 12 12.75C10.615 12.75 9.25001 11.79 9.25001 10.333C9.25001 9.143 10.161 8.285 11.25 8.01V8C11.25 7.80109 11.329 7.61032 11.4697 7.46967C11.6103 7.32902 11.8011 7.25 12 7.25Z" fill="#F97316" />
                            </svg>
                        </div>
                        <h3 className={s.featureTitle}>Lead-generation fee by state</h3>
                        <p className={s.featureDescription}>You’ll be charged a lead-gen fee for each new patient booking, based on your selected specialty {leadGenFee?.specialty} and location. If the patient cancels within that 24-hour window, you will not be charged.</p>
                        <LeadGenFeeTable statesAndFeeMap={statesAndFeeMap} />
                    </div>
                </div>
                <div className={s.featuresGrid}>
                    <div className={s.featuresColumn}>
                        {featureItems.map((item) => (
                            <div key={item.id} className={s.featureCard}>
                                <div className={s.iconWrapper}>{item.icon}</div>
                                <h3 className={s.featureTitle}>{item.title}</h3>
                                <p className={s.featureDescription}>
                                    {item.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Payment Information or Add Payment Method Button */}
                {hasPaymentInfo ? (
                    <div className={s.paymentInfoCard}>
                        <div className={s.paymentInfoHeader}>
                            <div className={s.paymentInfoTitle}>
                                <span>Payment information</span>
                                <div className={s.stripTip} />
                            </div>
                            <Button
                                type="default"
                                size="small"
                                onClick={handleEditPayment}
                                className={s.editPaymentBtn}
                            >
                                Edit
                            </Button>
                        </div>

                        {/* 根据payment type渲染不同的内容 */}
                        {
                            paymentInfo?.type === 'us_bank_account' ? (
                                // 银行账户信息
                                <div className={s.bankInfoContent}>
                                    <div className={s.paymentInfoField}>
                                        <label>Account name</label>
                                        <span className={s.paymentInfoValue}>{paymentInfo?.accountName}</span>
                                    </div>
                                    <div className={s.paymentInfoField}>
                                        <label>Bank name</label>
                                        <span className={s.paymentInfoValue}>{paymentInfo?.bankName}</span>
                                    </div>
                                    <div className={s.paymentInfoField}>
                                        <label>Card number</label>
                                        <span className={s.paymentInfoValue}>**** **** **** {paymentInfo?.last4}</span>
                                    </div>
                                    <div className={s.paymentInfoField}>
                                        <label>Country</label>
                                        <span className={s.paymentInfoValue}>{paymentInfo?.country}</span>
                                    </div>
                                    <div className={s.paymentInfoField}>
                                        <label>Postal code</label>
                                        <span className={s.paymentInfoValue}>{paymentInfo?.postalCode}</span>
                                    </div>
                                </div>
                            ) : (
                                // 信用卡信息
                                <div>
                                    <div className={s.paymentInfoContent}>
                                        <div className={s.paymentInfoField}>
                                            <label>Card number</label>
                                            <span className={s.paymentInfoValue}>**** **** **** {paymentInfo?.last4}</span>
                                        </div>
                                        <div className={s.paymentInfoField}>
                                            <label>Expiration date</label>
                                            <span className={s.paymentInfoValue}>
                                                {String(paymentInfo?.expMonth).padStart(2, '0')}/
                                                {paymentInfo?.expYear ? String(paymentInfo.expYear).slice(-2) : ''}
                                            </span>
                                        </div>
                                        <div className={s.paymentInfoField}>
                                            <label>CVC</label>
                                            <span className={s.paymentInfoValue}>***</span>
                                        </div>
                                        <div className={s.paymentInfoField}>
                                            <label>Country</label>
                                            <span className={s.paymentInfoValue}>{paymentInfo?.country}</span>
                                        </div>
                                        <div className={s.paymentInfoField}>
                                            <label>Postal code</label>
                                            <span className={s.paymentInfoValue}>{paymentInfo?.postalCode}</span>
                                        </div>
                                    </div>
                                    <div className={s.paymentInfoField}>
                                        <label>Expiration date</label>
                                        <span className={s.paymentInfoValue}>
                                            {String(paymentInfo?.expMonth).padStart(
                                                2,
                                                '0',
                                            )}
                                            /
                                            {paymentInfo?.expYear
                                                ? String(paymentInfo.expYear).slice(
                                                    -2,
                                                )
                                                : ''}
                                        </span>
                                    </div>
                                    <div className={s.paymentInfoField}>
                                        <label>CVC</label>
                                        <span className={s.paymentInfoValue}>
                                            ***
                                        </span>
                                    </div>
                                    <div className={s.paymentInfoField}>
                                        <label>Country</label>
                                        <span className={s.paymentInfoValue}>
                                            {paymentInfo?.country}
                                        </span>
                                    </div>
                                    <div className={s.paymentInfoField}>
                                        <label>Postal code</label>
                                        <span className={s.paymentInfoValue}>
                                            {paymentInfo?.postalCode}
                                        </span>
                                    </div>
                                </div>
                            )}
                    </div>
                ) : (
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAddPaymentMethod}
                        className={s.addPaymentBtn}
                    >
                        Add payment method
                    </Button>
                )}
            </div>
        </div>
    );
};

export default Step5;
